import { useEffect, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

/** Edit a vendor's contact person, phone and email. RFQ links are sent to this phone (WhatsApp) and email. */
export function VendorContactDialog({ vendor, onSaved, trigger, testId, open: controlledOpen, onOpenChange }) {
  const [innerOpen, setInnerOpen] = useState(false);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : innerOpen;
  const setOpen = controlled ? onOpenChange : setInnerOpen;
  const [form, setForm] = useState({ contact_person: "", phone: "", email: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setErrors({}); setForm({ contact_person: vendor.contact_person || "", phone: vendor.phone || "", email: vendor.email || "" }); }
  }, [open, vendor]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setSaving(true); setErrors({});
    try {
      await api.patch(`/procurement/vendors/${vendor.id}/contact`, form);
      toast.success(`${vendor.name} contact details saved`);
      setOpen(false); onSaved?.();
    } catch (err) {
      setErrors(getFieldErrors(err)); toast.error(getErrorMessage(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && (
        <DialogTrigger asChild>
          {trigger || <Button size="sm" variant="ghost" className="h-7 px-2" title="Edit phone and email" data-testid={testId || `vendor-contact-button-${vendor.code}`}><Pencil className="h-3.5 w-3.5" /></Button>}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{vendor.name} — contact details</DialogTitle>
          <DialogDescription>Quotation links are sent to this WhatsApp number and email address.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3.5" data-testid="vendor-contact-form">
          <div><Label>Contact person</Label><Input value={form.contact_person} onChange={set("contact_person")} className="mt-1.5" data-testid="vendor-contact-person" /></div>
          <div><Label>Phone / WhatsApp</Label><Input inputMode="tel" value={form.phone} onChange={set("phone")} placeholder="98765 43210 or +971 50 123 4567" className="mt-1.5" data-testid="vendor-contact-phone" /><p className="mt-0.5 text-[10px] text-muted-foreground">A 10-digit Indian number is treated as +91. Add the country code for any other country.</p><FieldError error={errors.phone} /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} placeholder="sales@vendor.com" className="mt-1.5" data-testid="vendor-contact-email" /><FieldError error={errors.email} /></div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="vendor-contact-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
